import React, {
 useState, useEffect
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Plus, Trash2
} from 'lucide-react';
import {
 schemaService
} from 'shared/api/services/BEXO';
import {
 MutateStepConfig, WorkflowStep
} from '../../types/workflow.types';
import PlaceholderHelper from './PlaceholderHelper';

interface MutateStepFormProps {
    config: MutateStepConfig;
    onChange: (config: MutateStepConfig) => void;
    steps: WorkflowStep[];
    stepIndex: number;
}

const operations = ['INSERT', 'UPDATE', 'DELETE'] as const;

const operationColors: Record<string, string> = {
    INSERT: 'border-green-500 bg-green-50 text-green-700',
    UPDATE: 'border-amber-500 bg-amber-50 text-amber-700',
    DELETE: 'border-red-500 bg-red-50 text-red-700',
};

const MutateStepForm: React.FC<MutateStepFormProps> = ({
 config, onChange, steps, stepIndex
}) => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const [schemaNames, setSchemaNames] = useState<string[]>([]);
    const [schemaColumns, setSchemaColumns] = useState<string[]>([]);
    const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);

    // Load schema names
    useEffect(() => {
        const loadSchemas = async () => {
            if (!appId || !oragID) return;
            try {
                const res = await schemaService.retrieveSchemas('*', appId, oragID);
                const rawSchemas = res.data?.schemas || res.data || [];
                if (Array.isArray(rawSchemas)) {
                    const names = new Set<string>();
                    rawSchemas.forEach((item: any) => {
                        const name = item.tableName || item.table_name;
                        if (name) names.add(name);
                    });
                    setSchemaNames(Array.from(names));
                }
            } catch (error) {
                console.error('Error loading schemas:', error);
            } finally {
                setIsLoadingSchemas(false);
            }
        };
        loadSchemas();
    }, [appId, oragID]);

    // Load columns when schema changes
    useEffect(() => {
        const loadColumns = async () => {
            if (!config.schemaName || !appId || !oragID) {
                setSchemaColumns([]);
                return;
            }
            try {
                const res = await schemaService.retrieveSchemas(config.schemaName, appId, oragID);
                const rawSchemas = res.data?.schemas || res.data || [];
                const columns: string[] = [];
                if (Array.isArray(rawSchemas)) {
                    rawSchemas.forEach((item: any) => {
                        if (Array.isArray(item.columns)) {
                            item.columns.forEach((col: any) => {
                                if (col.columnName) columns.push(col.columnName);
                            });
                        } else if (item.columnName) {
                            columns.push(item.columnName);
                        }
                    });
                }
                setSchemaColumns(columns);
            } catch (error) {
                console.error('Error loading columns:', error);
            }
        };
        loadColumns();
    }, [config.schemaName, appId, oragID]);

    const addFieldMapping = () => {
        onChange({
            ...config,
            fieldMappings: [...config.fieldMappings, {
 fieldName: '',
value: ''
}],
        });
    };

    const updateFieldMapping = (index: number, update: Partial<{ fieldName: string; value: string }>) => {
        const newMappings = config.fieldMappings.map((m, i) => (i === index ? {
 ...m,
...update
} : m));
        onChange({
 ...config,
fieldMappings: newMappings
});
    };

    const removeFieldMapping = (index: number) => {
        onChange({
 ...config,
fieldMappings: config.fieldMappings.filter((_, i) => i !== index)
});
    };

    const handlePlaceholderInsert = (placeholder: string) => {
        onChange({
 ...config,
condition: (config.condition || '') + placeholder
});
    };

    const showFieldMappings = config.operation === 'INSERT' || config.operation === 'UPDATE';
    const showCondition = config.operation === 'UPDATE' || config.operation === 'DELETE';

    return (
        <div className="space-y-4">
            {/* Operation */}
            <div className="space-y-1.5">
                <Label className="text-sm">Operation</Label>
                <div className="grid grid-cols-3 gap-2">
                    {operations.map((op) => (
                        <button
                            key={op}
                            type="button"
                            className={`p-2 border rounded-md text-center text-sm font-medium transition-colors ${
                                config.operation === op
                                    ? operationColors[op]
                                    : 'border-muted hover:bg-muted/50'
                            }`}
                            onClick={() => onChange({
 ...config,
operation: op
})}
                        >
                            {op}
                        </button>
                    ))}
                </div>
            </div>

            {/* Target Schema */}
            <div className="space-y-1.5">
                <Label className="text-sm">Target Schema</Label>
                <Select
                    value={config.schemaName}
                    onValueChange={(v) => onChange({
 ...config,
schemaName: v,
fieldMappings: []
})}
                    disabled={isLoadingSchemas}
                >
                    <SelectTrigger>
                        <SelectValue placeholder={isLoadingSchemas ? 'Loading...' : 'Select a schema'} />
                    </SelectTrigger>
                    <SelectContent>
                        {schemaNames.map((name) => (
                            <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Field Mappings */}
            {showFieldMappings && (
                <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                        <Label className="text-sm">Field Mappings</Label>
                        <Button variant="ghost" size="sm" onClick={addFieldMapping} className="h-7 text-xs">
                            <Plus className="mr-1 h-3 w-3" /> Add Mapping
                        </Button>
                    </div>
                    {config.fieldMappings.length > 0 && (
                        <div className="space-y-2">
                            {config.fieldMappings.map((mapping, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <Select
                                        value={mapping.fieldName}
                                        onValueChange={(v) => updateFieldMapping(idx, {
 fieldName: v
})}
                                    >
                                        <SelectTrigger className="flex-1">
                                            <SelectValue placeholder="Field" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {schemaColumns.map((col) => (
                                                <SelectItem key={col} value={col}>{col}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        value={mapping.value}
                                        onChange={(e) => updateFieldMapping(idx, {
 value: e.target.value
})}
                                        placeholder="Value or {{step_N.field}}"
                                        className="flex-1"
                                    />
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-red-500 shrink-0"
                                        onClick={() => removeFieldMapping(idx)}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Condition */}
            {showCondition && (
                <div className="space-y-1.5">
                    <Label className="text-sm">Condition (WHERE)</Label>
                    <Input
                        value={config.condition}
                        onChange={(e) => onChange({
 ...config,
condition: e.target.value
})}
                        placeholder="e.g., id = {{step_1.id}}"
                    />
                </div>
            )}

            {/* Placeholder Helper */}
            <PlaceholderHelper
                steps={steps}
                currentStepIndex={stepIndex}
                onInsert={handlePlaceholderInsert}
            />
        </div>
    );
};

export default MutateStepForm;
